import { Box, Typography, Container } from '@mui/material';
import { Link } from 'react-router-dom';

function TermsPage() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flex: 1, py: 4 }}>
        <Link to="/" style={{ color: '#1976d2', fontSize: 14, textDecoration: 'none' }}>
          &larr; Back
        </Link>

        <Typography variant="h4" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
          Terms of Service &amp; End User License Agreement
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 4 }}>
          Last updated: April 17, 2026
        </Typography>

        <Section title="1. Acceptance of Terms">
          By creating an account or using Third Place, you agree to these Terms of Service and End
          User License Agreement ("Terms"). These Terms constitute a legally binding agreement
          between you and Third Place. If you do not agree to every part of these Terms, do not
          create an account and do not use the app.
        </Section>

        <Section title="2. Description of Service">
          Third Place is a location-based social app for organizing spontaneous, real-world meetups
          with friends. Features include creating events and pings, real-time messaging, photo
          sharing in chats, polls in conversations, live location sharing with friends, friend
          management, and location-based discovery.
        </Section>

        <Section title="3. User Accounts">
          You must provide accurate information when creating an account. You are responsible for
          maintaining the security of your account credentials. You must be at least 13 years old to
          use Third Place. One person may not maintain more than one account.
        </Section>

        <Section title="4. Zero Tolerance for Objectionable Content and Abusive Behavior">
          Third Place has <b>zero tolerance</b> for objectionable content and abusive users. You
          agree that you will not, under any circumstances, post, upload, transmit, or otherwise
          make available content that is:
          <ul>
            <li>Harassing, bullying, threatening, or hateful toward any person or group;</li>
            <li>
              Sexually explicit, pornographic, or sexually suggestive, including nudity and
              sexualized content involving minors;
            </li>
            <li>Violent, graphic, or that depicts or glorifies harm to people or animals;</li>
            <li>Discriminatory on the basis of race, ethnicity, religion, gender, sexual orientation, disability, or any other protected class;</li>
            <li>Illegal, or that promotes, facilitates, or depicts illegal activity;</li>
            <li>
              Fraudulent, deceptive, spam, or intended to scam or manipulate other users;
            </li>
            <li>Infringing on intellectual property, privacy, or other rights of third parties;</li>
            <li>Impersonating another person or entity;</li>
            <li>Otherwise objectionable, offensive, or inappropriate.</li>
          </ul>
          You also agree not to engage in abusive behavior toward other users, including but not
          limited to unwanted contact, stalking, doxxing, intimidation, or attempts to meet up with
          minors or other vulnerable individuals without proper consent.
        </Section>

        <Section title="5. Content Moderation and Enforcement">
          Third Place reserves the right, at our sole discretion and without prior notice, to
          review, remove, or refuse to display any content you submit, and to suspend, terminate,
          or permanently ban your account for any violation of these Terms — especially violations
          of Section 4.
          <br />
          <br />
          By using the app, you consent to automated and manual moderation of content you submit.
          This may include scanning messages, event descriptions, photos, and other user-generated
          content to identify potentially objectionable material. You also consent to receiving
          content filtering features, such as profanity masking, that other users may have enabled.
          <br />
          <br />
          We act on reports of objectionable content or abusive users <b>within 24 hours</b> of
          receiving them. In that time, we will review the report, remove the offending content if
          warranted, and take action against the user who submitted it — up to and including
          permanent removal from the service.
        </Section>

        <Section title="6. Reporting and Blocking">
          If you encounter objectionable content or an abusive user, you have tools to address it
          directly inside the app:
          <ul>
            <li>
              <b>Report</b> any message, event, conversation, or user from that item's menu. Your
              report is reviewed by our team within 24 hours.
            </li>
            <li>
              <b>Block</b> any user from the Friends screen. Blocking removes the user from all
              shared conversations, hides their events, and severs any further interaction between
              you and them instantly. Blocking is a private action and does not notify the blocked
              user.
            </li>
          </ul>
          Reporting and blocking are separate actions. We encourage you to report content that
          violates these Terms so we can take action against the offending user, not only for you
          but for the broader community.
        </Section>

        <Section title="7. Privacy">
          Your use of Third Place is also governed by our{' '}
          <Link to="/privacy" style={{ color: '#1976d2' }}>
            Privacy Policy
          </Link>
          , which describes how we collect, use, and protect your information.
        </Section>

        <Section title="8. Intellectual Property">
          Third Place and its original content, features, and functionality are owned by Third
          Place and are protected by copyright and other intellectual property laws. You retain
          ownership of content you create, but grant us a worldwide, royalty-free license to host,
          store, display, and distribute that content within the app as needed to provide the
          service, and to moderate that content in line with these Terms.
        </Section>

        <Section title="9. Termination">
          We may suspend or terminate your account at any time, with or without notice, if you
          violate these Terms — including any violation of Section 4. You may delete your account
          at any time from the Settings screen. Upon termination, your data will be permanently
          deleted as described in our Privacy Policy. Users who are ejected from the service for
          violating these Terms are not entitled to create new accounts.
        </Section>

        <Section title="10. Disclaimers">
          Third Place is provided "as is" without warranties of any kind. We do not guarantee the
          app will be available at all times or free from errors. We are not responsible for the
          conduct of other users or the content they post. Use caution when meeting people in
          person based on events or pings in the app. If you use live location sharing, exercise
          caution and only share your real-time location with people you trust. You are responsible
          for deciding when to enable and disable location sharing.
        </Section>

        <Section title="11. Limitation of Liability">
          To the fullest extent permitted by law, Third Place shall not be liable for any indirect,
          incidental, special, or consequential damages arising from your use of the app, including
          damages arising from content posted by other users.
        </Section>

        <Section title="12. Changes to Terms">
          We may modify these Terms at any time. When we make material changes, we will notify you
          within the app and require you to re-accept the updated Terms before continuing to use
          the service. Continued use after changes constitutes acceptance of the new Terms.
        </Section>

        <Section title="13. Contact">
          For questions about these Terms, to report content or users, or to appeal a moderation
          decision, contact us at{' '}
          <a href="mailto:legal@thirdplaceapp.com">legal@thirdplaceapp.com</a>. We respond to all
          inquiries within 24 hours.
        </Section>
      </Container>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  );
}

export default TermsPage;
